import { Icon } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiEye } from "@mdi/js";
import { TabContext, TabPanel } from "@mui/lab";
import {
  Box,
  IconButton,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
} from "@mui/material";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CreatePurchaseOrderByServiceQuotation } from "../../../../@types/services/CreatePurchaseOrderByServiceQuotation";
import { CommonErrorHelp } from "../../../../components/molecules/CommonErrorHelp";
import {
  getCreatePurchaseOrderByServiceQuotation,
  listCreatePurchaseOrderByServiceQuotations,
} from "../../../../services/createPurchaseOrderByServiceQuotations";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";

interface CreatePurchaseOrderByServiceQuotationRowProps {
  createPurchaseOrderByServiceQuotationId: string;
}

const CreatePurchaseOrderByServiceQuotationRow: React.FC<
  CreatePurchaseOrderByServiceQuotationRowProps
> = ({ createPurchaseOrderByServiceQuotationId }) => {
  const { t } = useTranslation();

  const [data, setData] = useState<CreatePurchaseOrderByServiceQuotation>();
  const [loading, setLoading] = useState(true);

  const columnQuantity = useMemo(() => 4, []);

  const handleFetch = useCallback((id: string): void => {
    setLoading(() => true);
    getCreatePurchaseOrderByServiceQuotation(id)
      .then((response) => {
        setData(() => response);
      })
      .finally(() => {
        setLoading(() => false);
      });
  }, []);

  useEffect(() => {
    handleFetch(createPurchaseOrderByServiceQuotationId);
  }, [createPurchaseOrderByServiceQuotationId, handleFetch]);

  if (loading) {
    return (
      <TableRow key={createPurchaseOrderByServiceQuotationId}>
        {Array(columnQuantity).map(() => (
          <TableCell key={`skeleton-cell-${crypto.randomUUID()}`}>
            <Skeleton />
          </TableCell>
        ))}
      </TableRow>
    );
  }

  if (!data) {
    return (
      <TableRow key={createPurchaseOrderByServiceQuotationId}>
        <TableCell colSpan={columnQuantity}>Fail to load</TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow key={data.id}>
      <TableCell>
        <span>{t("intlDateTime", i18nextParseToDate(data.created_at))}</span>
      </TableCell>

      <TableCell>
        <span>{t("intlDateTime", i18nextParseToDate(data.updated_at))}</span>
      </TableCell>

      <TableCell align="center">
        {data.completed ? <span>{t("yes")}</span> : <span>{t("no")}</span>}
      </TableCell>

      <TableCell align="center">
        <Tooltip title={t("view")}>
          <IconButton component={Link} to={data.id}>
            <Icon icon={mdiEye} />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

type CreatePurchaseOrderByServiceQuotationTableProps = {
  completed: boolean;
};

const CreatePurchaseOrderByServiceQuotationTable: React.FC<
  CreatePurchaseOrderByServiceQuotationTableProps
> = ({ completed }) => {
  const { t } = useTranslation();

  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [
    createPurchaseOrderByServiceQuotations,
    setCreatePurchaseOrderByServiceQuotations,
  ] = useState<CreatePurchaseOrderByServiceQuotation[]>([]);
  const [loading, setLoading] = useState(true);

  const handleFetch = useCallback(
    async (page: number) => {
      setLoading(() => true);
      await listCreatePurchaseOrderByServiceQuotations({
        filterCompleted: completed,
        itemsPerPage: rowsPerPage,
        order: "desc",
        page,
      })
        .then((createPurchaseOrderByServiceQuotation) => {
          setCreatePurchaseOrderByServiceQuotations(
            () => createPurchaseOrderByServiceQuotation.results || [],
          );
          setTotalRows(
            () => createPurchaseOrderByServiceQuotation.total_items || 0,
          );
        })
        .finally(() => {
          setLoading(() => false);
        });
    },
    [completed, rowsPerPage],
  );

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    value: number,
  ): void => {
    setCurrentPage(() => value + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setRowsPerPage(() => parseInt(event.target.value, 10));
    setCurrentPage(() => 1);
  };

  useEffect(() => {
    handleFetch(currentPage);
  }, [currentPage, handleFetch]);

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            <TableCell align="left">
              {t("createPurchaseOrderByServiceQuotation.createdAt")}
            </TableCell>
            <TableCell align="left">
              {t("createPurchaseOrderByServiceQuotation.updatedAt")}
            </TableCell>
            <TableCell align="center">
              {t("createPurchaseOrderByServiceQuotation.completed")}
            </TableCell>
            <TableCell align="center" />
          </TableRow>
        </TableHead>
        <TableBody>
          {!!loading && (
            <TableRow key="loading-skeleton">
              {Array(4).map(() => (
                <TableCell key={`skeleton-cell-${crypto.randomUUID()}`}>
                  <Skeleton />
                </TableCell>
              ))}
            </TableRow>
          )}
          {!loading &&
            createPurchaseOrderByServiceQuotations &&
            createPurchaseOrderByServiceQuotations.map(
              (createPurchaseOrderByServiceQuotation) => (
                <CreatePurchaseOrderByServiceQuotationRow
                  createPurchaseOrderByServiceQuotationId={
                    createPurchaseOrderByServiceQuotation.id
                  }
                  key={createPurchaseOrderByServiceQuotation.id}
                />
              ),
            )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              count={totalRows}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              page={currentPage - 1}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              showFirstButton
              showLastButton
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

const CreatePurchaseOrderByServiceQuotations: React.FC = () => {
  const { t } = useTranslation();

  const [tabOpen, setTabOpen] = useState("notCompleted");

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ): void => {
    setTabOpen(() => newValue);
  };

  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "100%",
        justifyContent: "center",
        px: 2,
        width: "100%",
      }}
    >
      <h1>{t("createPurchaseOrderByServiceQuotation.title")}</h1>

      <TabContext value={tabOpen}>
        <Box
          sx={{
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Tabs onChange={handleTabChange} value={tabOpen}>
            <Tab
              label={t("createPurchaseOrderByServiceQuotation.notCompleted")}
              value="notCompleted"
            />
            <Tab
              label={t("createPurchaseOrderByServiceQuotation.completed")}
              value="completed"
            />
          </Tabs>
          <CommonErrorHelp errorType="create_purchase_order_service_quotation" />
        </Box>

        <TabPanel value="notCompleted">
          <CreatePurchaseOrderByServiceQuotationTable completed={false} />
        </TabPanel>

        <TabPanel value="completed">
          <CreatePurchaseOrderByServiceQuotationTable completed />
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/create_purchase_order_by_service_quotations/",
)({
  component: CreatePurchaseOrderByServiceQuotations,
});
